import React, { useMemo, useState } from "react";
import { Steps, Form } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import StyledModal from "../../../components/CustomModal";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { FooterButtons, StyledButton } from "../../../components/styles";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/redux";
import { useFetchReport } from "../../../utils/hooks/useFetchReport";
import { handleAddMergeDateTime, handleMergeDateTime } from "../../../utils/helpers/dateFunctions";
import { createLostItemByAdmin, updateLostItemByAdmin } from "../../../redux/features/report/reportAsyncThunk";
import { setQuestionData } from "../../../redux/features/question/questionSlice";
import { convertIntoBoardTicket, updateTicketLocally } from "../../../redux/features/board/columnsSlice";
import { ReportType } from "../../../redux/features/board/columnTypes";
import { Report } from "../../../types";
import UserDetailsForm from "./steps/UserDetailsForm";
import ItemDetailsForm from "./steps/ItemDetailsForm";
import LocationDetailsForm from "./steps/LocationDetailsForm";

// Define interface for answer object (local to this component)
interface Answer {
  question: string;
  answer: string | boolean;
}

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

interface EditReportDetailsModalProps {
  isModalOpen: boolean;
  handleOk: () => void;
  handleCancel: () => void;
  report: Report | null;
}

const EditReportDetailsModal: React.FC<EditReportDetailsModalProps> = ({ isModalOpen, handleOk, handleCancel, report }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const { loading } = useAppSelector((state) => state.report);
  const [showEmailSendConfirm, setShowEmailSendConfirm] = useState(false);
  const { categoryState, subCategoryState, establishmentTypeQuestionState } = useAppSelector((state) => state.generaleWebData);
  const { data: questions } = useAppSelector((state) => state.question);
  const { status: categoryStatus } = useMemo(() => categoryState, [categoryState]);
  const { status: subCategoryStatus } = useMemo(() => subCategoryState, [subCategoryState]);
  const { establishmentTypeQuestions } = useMemo(() => establishmentTypeQuestionState, [establishmentTypeQuestionState]);

  const [formData, setFormData] = useState<any>(null);

  const dispatch = useAppDispatch();
  const fetchReport = useFetchReport();

  const steps = [
    {
      title: "Location Details",
      content: <LocationDetailsForm form={form} report={report} />,
    },
    {
      title: "Item Details",
      content: <ItemDetailsForm form={form} report={report} />,
    },
    {
      title: "Personal Details",
      content: <UserDetailsForm form={form} report={report} />,
    },
  ];

  const handleNext = async () => {
    try {
      const values = await form.validateFields();
      setFormData((prev: any) => ({ ...prev, ...values }));
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const fieldsToValidate = [
    "firstName",
    "lastName",
    "country",
    "state",
    "city",
    "stateCode",
    "countryCode",
    "address1",
    "address2",
    "zip",
    "placeId",
    "terms",
  ];

  const handleFinish = async (sendEmail: boolean) => {
    try {

      if ((!report?.id || !report?.trackingId) && !report?.isCreate) return;

      // Get reference to the ItemDetailsForm component
      const itemFormRef = document.getElementById('item-details-form');

      // If the form is at the item details step, ensure we update all answers
      if (currentStep === 1 && itemFormRef) {
        // Update all items with their latest answers before form submission
        // This ensures we have the most up-to-date answers for all items
        form.setFieldsValue({
          items: form.getFieldValue('items')?.map((item: any, index: number) => {
            // Build the latest answers for this item
            const answers: Answer[] = [];

            // Get all question answers for this item from form fields
            Object.keys(form.getFieldsValue()).forEach((key) => {
              if (key.startsWith(`item_${index}_question_`)) {
                const questionId = key.replace(`item_${index}_question_`, '');
                const answerValue = form.getFieldValue(key);

                if (answerValue !== undefined && answerValue !== '' && answerValue !== null) {
                  answers.push({
                    question: questionId,
                    answer: answerValue
                  });
                }
              }
            });

            return {
              ...item,
              answers
            };
          })
        });
      }

      const values = await form.validateFields();
      const userValues = await form.validateFields(fieldsToValidate);

      const updatedFormData = { ...formData, ...values, ...userValues };

      const answers =
        questions?.length > 0
          ? questions.map((question) => {
            // Get the answer for the main question
            const answerUUID = updatedFormData[`question_${question.id}`]; // Store the UUID of the answer
            delete updatedFormData[`question_${question.id}`]; // Remove the answer from values

            // Find the corresponding name of the answer for the main question
            const selectedOption = question.options?.find((option) => option.id === answerUUID);
            const answerName = selectedOption ? selectedOption.name ?? "" : answerUUID; // If found, use name, else keep UUID
            const parentID = questions?.length > 0 ? questions.find((question) => question.options?.some((option) => option.id === selectedOption?.id))?.id : null;

            // Start by storing the main question's answer with its name
            const combinedAnswers = [{ question: question.id, answer: answerName }];

            // If the current question is the "Brand" question (ID: 643f1b0e-fd7a-444f-b00e-ff97fa816497), remove subQuestions
            if (question.id === parentID) {
              // Remove subQuestion answers for this question
              question.options?.forEach((option) => {
                if (option?.name === selectedOption?.name) {
                  // Keep the subQuestion answers for "Samsung"
                  option?.subQuestion?.forEach((subQ: any) => {
                    const subAnswerUUID = updatedFormData[`subQuestion_${subQ.id}`]; // Get the UUID for subQuestion answer
                    if (subAnswerUUID !== undefined) {
                      // Find the corresponding subQuestion name
                      const selectedSubOption = option?.subQuestion?.find((sub: any) => sub.id === subAnswerUUID);
                      const subAnswerName = selectedSubOption ? selectedSubOption.fieldName : subAnswerUUID; // If found, use fieldName, else keep UUID

                      // Add subQuestion answer to the list with its name
                      combinedAnswers.push({ question: subQ.id, answer: subAnswerName });
                    }
                    // Remove subQuestion answer from values
                    delete updatedFormData[`subQuestion_${subQ.id}`];
                  });
                } else {
                  option?.subQuestion?.forEach((subQ: any) => {
                    delete updatedFormData[`subQuestion_${subQ.id}`]; // Remove subQuestion answer from values
                  });
                }
              });
            } else {
              // Otherwise, check if there are subQuestions for this question and handle them
              question.options?.forEach((option) => {
                option?.subQuestion?.forEach((subQ: any) => {
                  const subAnswerUUID = updatedFormData[`subQuestion_${subQ.id}`]; // Get the UUID for subQuestion answer
                  if (subAnswerUUID !== undefined) {
                    // Find the corresponding subQuestion name
                    const selectedSubOption = option?.subQuestion?.find((sub: any) => sub.id === subAnswerUUID);
                    const subAnswerName = selectedSubOption ? selectedSubOption.fieldName : subAnswerUUID; // If found, use fieldName, else keep UUID

                    // Add subQuestion answer to the list with its name
                    combinedAnswers.push({ question: subQ.id, answer: subAnswerName });
                  }
                  // Remove subQuestion answer from values
                  delete updatedFormData[`subQuestion_${subQ.id}`];
                });
              });
            }

            // Return the combined list of main question + subQuestion
            return combinedAnswers?.filter((answer) => answer.answer !== undefined);
          })
          : [];

      const establishmentTypeAnswers =
        establishmentTypeQuestions?.length > 0
          ? establishmentTypeQuestions.map((question) => {
            const answerValue = updatedFormData[`establishmentTypeQuestion_${question.id}`]; // Get the answer value
            delete updatedFormData[`establishmentTypeQuestion_${question.id}`]; // Remove the answer from values

            let answerName = answerValue;
            let selectedOption: any = null;

            // Handle different field types
            if (question.fieldType === 1) {
              // Text input - use the value directly
              answerName = answerValue || "";
            } else if (question.fieldType === 2) {
              // Checkbox - convert boolean to string
              answerName = answerValue === true ? "true" : "false";
            } else if (question.fieldType === 3) {
              // Dropdown - find the option name
              selectedOption = question.options?.find((option) => option.id === answerValue);
              answerName = selectedOption ? selectedOption.name ?? "" : answerValue;
            }

            const parentID = establishmentTypeQuestions?.length > 0 ? establishmentTypeQuestions.find((question) => question.options?.some((option) => option.id === selectedOption?.id))?.id : null;

            const combinedAnswers = [{ question: question.id, answer: answerName }];

            if (question.id === parentID) {
              question.options?.forEach((option) => {
                if (option?.name === selectedOption?.name) {
                  option?.subQuestion?.forEach((subQ: any) => {
                    const subAnswerUUID = updatedFormData[`establishmentTypeSubQuestion_${subQ.id}`]; // Get the UUID for subQuestion answer
                    if (subAnswerUUID !== undefined) {
                      const selectedSubOption = option?.subQuestion?.find((sub: any) => sub.id === subAnswerUUID);
                      const subAnswerName = selectedSubOption ? selectedSubOption.fieldName : subAnswerUUID; // If found, use fieldName, else keep UUID

                      combinedAnswers.push({ question: subQ.id, answer: subAnswerName });
                    }
                    delete updatedFormData[`establishmentTypeSubQuestion_${subQ.id}`];
                  });
                } else {
                  option?.subQuestion?.forEach((subQ: any) => {
                    delete updatedFormData[`establishmentTypeSubQuestion_${subQ.id}`]; // Remove subQuestion answer from values
                  });
                }
              });
            } else {
              question.options?.forEach((option) => {
                option?.subQuestion?.forEach((subQ: any) => {
                  const subAnswerUUID = updatedFormData[`establishmentTypeSubQuestion_${subQ.id}`]; // Get the UUID for subQuestion answer
                  if (subAnswerUUID !== undefined) {
                    const selectedSubOption = option?.subQuestion?.find((sub: any) => sub.id === subAnswerUUID);
                    const subAnswerName = selectedSubOption ? selectedSubOption.fieldName : subAnswerUUID; // If found, use fieldName, else keep UUID

                    combinedAnswers.push({ question: subQ.id, answer: subAnswerName });
                  }
                  delete updatedFormData[`establishmentTypeSubQuestion_${subQ.id}`];
                });
              });
            }

            return combinedAnswers?.filter((answer) => answer.answer !== undefined && answer.answer !== "" && answer.answer !== null);
          })
          : [];

      // Flatten the answers into one list (because we might have multiple answers per question)
      const flattenedAnswers = answers?.flat()?.filter((answer) => answer.answer !== undefined);
      const flattenedEstablishmentTypeAnswers = establishmentTypeAnswers?.flat()?.filter((answer) => answer.answer !== undefined && answer.answer !== "" && answer.answer !== null);

      const locationAnswers = Object.entries({ ...formData, ...values, ...userValues })
        .filter(([key]) => key.startsWith("locationQuestion_"))
        .map(([key, value]) => ({
          question: key.replace("locationQuestion_", ""),
          answer: value,
        }));

      const cleanedFormData = { ...formData };

      Object.keys(cleanedFormData).forEach((key) => {
        if (key.startsWith("question_")) {
          delete cleanedFormData[key];
        } else if (key.startsWith("locationQuestion_")) {
          delete cleanedFormData[key];
        } else if (key.startsWith("subQuestion_")) {
          delete cleanedFormData[key];
        } else if (key.startsWith("establishmentTypeQuestion_")) {
          delete cleanedFormData[key];
        } else if (key.startsWith("establishmentTypeSubQuestion_")) {
          delete cleanedFormData[key];
        }
      });
      // Collect files from all items with item-specific keys
      const itemFiles: { [key: string]: any[] } = {};
      Object.keys({ ...formData, ...values }).forEach((key) => {
        if (key.startsWith('item_') && key.includes('_documents')) {
          const itemIndex = key.split('_')[1];
          const files = { ...formData, ...values }[key];
          if (Array.isArray(files) && files.length > 0) {
            const hasBlob = files.some((doc: any) => doc instanceof Blob);
            if (hasBlob) {
              itemFiles[`item_${itemIndex}_files`] = files;
            }
          }
        }
      });

      // Check for legacy documents field (fallback)
      let legacyFiles = [];
      if (Array.isArray(formData?.documents)) {
        const hasBlob = formData.documents.some((doc: any) => doc instanceof Blob);
        if (hasBlob) {
          legacyFiles = formData?.documents;
        }
      }

      delete cleanedFormData.documents;

      // Process items to ensure proper format
      const processedItems = cleanedFormData.items?.map((item: any, itemIndex: number) => {
        // Extract item answers from form data
        const itemAnswers: any[] = [];

        // Get all question answers for this item
        Object.keys({ ...formData, ...values }).forEach((key) => {
          if (key.startsWith(`item_${itemIndex}_question_`)) {
            const questionId = key.replace(`item_${itemIndex}_question_`, '');
            const answerValue = { ...formData, ...values }[key];

            if (answerValue !== undefined && answerValue !== "" && answerValue !== null) {
              itemAnswers.push({
                question: questionId,
                answer: answerValue
              });
            }
          }
          // Also collect subquestion answers
          if (key.startsWith(`item_${itemIndex}_subQuestion_`)) {
            const subQuestionId = key.replace(`item_${itemIndex}_subQuestion_`, '');
            const subAnswerValue = { ...formData, ...values }[key];

            if (subAnswerValue !== undefined && subAnswerValue !== "" && subAnswerValue !== null) {
              itemAnswers.push({
                question: subQuestionId,
                answer: subAnswerValue
              });
            }
          }
        });

        // Get the item description for this item from form data
        const itemDescription = { ...formData, ...values }[`item_${itemIndex}_itemDescription`];
        return {
          ...item,
          category: typeof item.category === 'object' ? item.category?.id : item.category,
          subCategory: typeof item.subCategory === 'object' ? item.subCategory?.id : item.subCategory,
          itemDescription: itemDescription || item.itemDescription,
          answers: itemAnswers.length > 0 ? itemAnswers : []
        };
      }) || [];

      const formattedValues = {
        ...cleanedFormData,
        ...values,
        ...userValues,
        items: processedItems,
        answers: flattenedAnswers?.length > 0 ? flattenedAnswers : JSON.stringify([]),
        establishmentTypeAnswers: flattenedEstablishmentTypeAnswers?.length > 0 ? flattenedEstablishmentTypeAnswers : JSON.stringify([]),
        locationAnswers: locationAnswers?.length > 0 ? locationAnswers : JSON.stringify([]),
        ...itemFiles, // Spread item-specific files (item_0_files, item_1_files, etc.)
        files: legacyFiles, // Keep legacy files for backward compatibility
        lostDate: dayjs(cleanedFormData.lostDate).isValid() ? (handleMergeDateTime(cleanedFormData.lostDate)?.toISOString() || dayjs(cleanedFormData.lostDate).toISOString()) : "",
        sendEmail
      };

      Object.keys(formattedValues).forEach((key) => {
        if (key.startsWith("question_") || key.startsWith("deletedFiles")) {
          delete (formattedValues as any)[key];
        }
        // Don't delete item_ keys that are files (item_0_files, item_1_files, etc.)
        if (key.startsWith("item_") && !key.includes("_files")) {
          delete (formattedValues as any)[key];
        }
      });

      if (report?.isCreate) {
        const response = await dispatch(createLostItemByAdmin({ formData: { ...formattedValues, foundItemId: report.foundItemId } })).unwrap();
        if (response) {
          dispatch(updateTicketLocally({ ticket: convertIntoBoardTicket({ ...response, isUrgent: report?.isUrgent }), type: ReportType.Lost }));
        }
      } else {
        await dispatch(updateLostItemByAdmin({ id: report?.id, formData: formattedValues }));
        await fetchReport(report?.trackingId);
      }
      form.resetFields();
      setFormData(null);
      setCurrentStep(0);
      handleOk();
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  const handleUpdate = async () => {
    try {
      await form.validateFields();
      await form.validateFields(fieldsToValidate);
      setShowEmailSendConfirm(true);
    } catch (error) {
      console.error("Form validation failed:", error);
    }
  };

  const isLoading = useMemo(() => loading || subCategoryStatus === "loading" || categoryStatus === "loading", [loading, subCategoryStatus, categoryStatus]);

  const handleOnClose = async () => {
    await dispatch(setQuestionData([]));
    handleCancel();
    setFormData(null);
    form.resetFields();
    setCurrentStep(0);
  };

  return (
    <StyledModal
      draggable={true}
      title={report?.isCreate ? "Create Lost Item" : "Update Lost Item"}
      open={isModalOpen}
      closeIcon={null}
      width={800}
      footer={
        <FooterButtons>
          <StyledButton onClick={() => handleOnClose()} disabled={loading}>
            Cancel
          </StyledButton>
          {currentStep > 0 && (
            <StyledButton onClick={handlePrev} disabled={loading}>
              Previous
            </StyledButton>
          )}
          {currentStep < steps.length - 1 ? (
            <StyledButton type="primary" onClick={handleNext} loading={isLoading} disabled={isLoading}>
              Next
            </StyledButton>
          ) : (
            <StyledButton type="primary" onClick={() => report?.isCreate ? handleFinish(true) : handleUpdate()} loading={isLoading} disabled={isLoading}>
              {report?.isCreate ? "Create" : "Update"}
            </StyledButton>
          )}
        </FooterButtons>
      }
      centered
      onCancel={handleOnClose}
    >
      <Steps style={{ marginTop: 24 }} current={currentStep} items={steps} />
      <div style={{ marginTop: 24 }}>{steps[currentStep].content}</div>

      {showEmailSendConfirm && (
        <ConfirmationModal
          isModalOpen={showEmailSendConfirm}
          onCancel={() => setShowEmailSendConfirm(false)}
          message={"Do you want to acknowledge your guest user about this updates?"}
          title={"Send Email"}
          loading={loading}
          handleSave={() => handleFinish(true)}
          CancelButtonText="No"
          confirmButtonText="Yes"
          footerButton={
            <FooterButtons>
              <StyledButton onClick={() => handleFinish(false)} disabled={loading}>{"No"}</StyledButton>
              <StyledButton type="primary" onClick={() => handleFinish(true)} loading={loading}>
                {"Yes"}
              </StyledButton>
            </FooterButtons>
          }
        />
      )}
    </StyledModal>
  );
};

export default EditReportDetailsModal;
